import * as Sentry from "@sentry/react";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useAuth,
  useClerk,
} from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  getCurrentScopes,
  getToken as getLexiunToken,
  setClerkTokenResolver,
} from "@/lib/lexiun-jwt";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { SentryFallback } from "./components/SentryFallback";

const queryClient = new QueryClient();
import { NavigationBridgeInstaller } from "@/components/platform-ops/NavigationBridgeInstaller";
import { SetupGate } from "@/components/contacts/SetupGate";
import { SetupReminderGate } from "@/components/contacts/SetupReminderGate";
import { ActivityBar } from "./components/shell/ActivityBar";
import { SidePanel } from "./components/shell/SidePanel";
import { RightSidebar } from "./components/shell/RightSidebar";
import { WorkspaceFilePaneShell } from "./components/workspace/WorkspaceFilePaneShell";
import { FocusPaneHost } from "./components/shell/FocusPaneHost";
import { useRailSpaceGovernor } from "./hooks/useRailSpaceGovernor";

/** Hook host — renders nothing; see `useRailSpaceGovernor`. */
function RailSpaceGovernor() {
  useRailSpaceGovernor();
  return null;
}
// Phase 7.3: route-level code splitting. Only the warmup-path routes
// stay eager (Home, /cases fallback, sign-in/up — anything a fresh
// page-load can land on directly). Everything else loads its chunk
// on first navigation, behind the <Suspense> boundary below. Drops the
// initial bundle from one ~1MB chunk to a small core + per-route chunks.
import { GlobalConversationLayout } from "./layouts/GlobalConversationLayout";
import { RecycleBinLayout } from "@/layouts/RecycleBinLayout";
import { SignInPage } from "./pages/SignInPage";
import { SignUpPage } from "./pages/SignUpPage";
import { SelectionUrlSync } from "./components/routing/SelectionUrlSync";
import { LastActiveContextSync } from "./components/routing/LastActiveContextSync";
import { MissionTickerBridge } from "./components/chat/MissionTickerBridge";
import { WorkspaceFileUrlSync } from "./components/routing/WorkspaceFileUrlSync";
// Dev-only probes — gated by `import.meta.env.DEV` below; in prod the
// branches are dead-code-eliminated so the import becomes orphaned and
// tree-shaken. Kept eager to keep the dev loop snappy.
import { TabRegistryProbe } from "./components/testing/TabRegistryProbe";
import { AssignmentsProbe } from "./components/testing/AssignmentsProbe";
import { ActiveContextPaneProbe } from "./components/testing/ActiveContextPaneProbe";
import { WorkspaceFilePaneProbe } from "./components/testing/WorkspaceFilePaneProbe";
import { DerivedSessionKeyProbe } from "./components/testing/DerivedSessionKeyProbe";
import { CaseWidgetsProbe } from "./components/testing/CaseWidgetsProbe";
// Lazy routes — `react-router-dom` v7 renders `element={<X />}` lazily
// when X is a `lazy()` component, so the chunk fetch only happens on
// the first navigation that matches the route. The Suspense boundary
// below holds the chrome (ActivityBar / SidePanel / Header) in place
// during the fetch so navigation feels native.
const WorkspaceLayout = lazy(() =>
  import("./layouts/WorkspaceLayout").then((m) => ({ default: m.WorkspaceLayout })),
);
const CaseWorkspaceLayout = lazy(() =>
  import("./layouts/CaseWorkspaceLayout").then((m) => ({ default: m.CaseWorkspaceLayout })),
);
const ContactsLayout = lazy(() => import("@/layouts/ContactsLayout"));
const TasklistLayout = lazy(() => import("@/layouts/TasklistLayout"));
const ScheduleLayout = lazy(() => import("@/layouts/ScheduleLayout"));
const IdentityLayout = lazy(() => import("@/layouts/IdentityLayout"));
const CaseRouteResolver = lazy(() =>
  import("./components/routing/CaseRouteResolver").then((m) => ({
    default: m.CaseRouteResolver,
  })),
);
const ActivityLayout = lazy(() =>
  import("./layouts/ActivityLayout").then((m) => ({ default: m.ActivityLayout })),
);
// `OrgAdminLayout` drags in the entire `@lexiun/org-admin` library +
// the Feedback page; lazy-loading is the single biggest win for
// non-admin users who never visit `/org/*`.
const OrgAdminLayout = lazy(() =>
  import("./layouts/OrgAdminLayout").then((m) => ({ default: m.OrgAdminLayout })),
);
const SettingsLayout = lazy(() =>
  import("./layouts/SettingsLayout").then((m) => ({ default: m.SettingsLayout })),
);
// Agent Browser — own chunk because it pulls in @novnc/novnc which
// would otherwise inflate the warmup bundle for users who never open
// the live pane.
const AgentBrowserLayout = lazy(() =>
  import("./layouts/AgentBrowserLayout").then((m) => ({
    default: m.AgentBrowserLayout,
  })),
);
// Agent (workspace settings — Personal, Channels, Notifications,
// Schedule, Voice, Browser CRUD, plus the ORG_ADMIN Agent + Memory &
// Tools tabs). Split out of SettingsLayout per the 2026-05-31
// operator direction so workspace config has its own top-nav slot.
const AgentLayout = lazy(() =>
  import("./layouts/AgentLayout").then((m) => ({
    default: m.AgentLayout,
  })),
);
import { resolveAppUrl } from "./lib/url";
import { isTypingTarget } from "./lib/keyboard";
import { classifyViewport, isCompactViewport, useViewportSize } from "./hooks/useViewportSize";
import { useSidePanelEntryGuarantee } from "./hooks/useSidePanelEntryGuarantee";
import { useUIStore } from "./stores/useUIStore";
import { useActiveContextStore } from "./stores/useActiveContextStore";
import { useCommandPaletteStore } from "./stores/useCommandPaletteStore";
import { GatewayProvider } from "./context/GatewayProvider";
import { UserActivityProvider } from "./lib/activity/activity-context";
import { PlatformOpsProvider } from "./components/platform-ops/PlatformOpsProvider";
import { ActivityFlusher } from "./components/activity-capture/ActivityFlusher";
import { useActivityCapture } from "./hooks/useActivityCapture";
import { useChatUnreadTracker } from "./hooks/useChatUnreadTracker";
import { useSelectionCapture } from "./hooks/useSelectionCapture";
import { useSelectionHighlight } from "./hooks/useSelectionHighlight";
import { useTeamPaneCommandsFromAgent } from "./hooks/useTeamPaneCommandsFromAgent";
import { useNavCommandsFromAgent } from "./hooks/useNavCommandsFromAgent";
import { useSettingsCommandsFromAgent } from "./hooks/useSettingsCommandsFromAgent";
import { useAgentDetailCommandsFromAgent } from "./hooks/useAgentDetailCommandsFromAgent";
import { useContextPaneCommandsFromAgent } from "./hooks/useContextPaneCommandsFromAgent";
import { useFileViewerCommandsFromAgent } from "./hooks/useFileViewerCommandsFromAgent";
import { useGuidanceCommandsFromAgent } from "./hooks/useGuidanceCommandsFromAgent";
import { MissionDetailDialog } from "./components/team-of-agents/MissionDetailDialog";
import { HarnessDetailDialog } from "./components/team-of-agents/HarnessDetailDialog";
import { CommandPalette } from "./components/search/CommandPalette";
import { ContextIndicator } from "./components/chat/ContextIndicator";
import { ShortcutHelpDialog } from "./components/shortcuts/ShortcutHelpDialog";
import { useShortcutHelpStore } from "./stores/useShortcutHelpStore";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";
import { GatewayDisconnectedBanner } from "./components/errors/GatewayDisconnectedBanner";
import { RetryPromptHost } from "./components/errors/RetryPrompt";
import { FeedbackOutboxRunner } from "./components/feedback/FeedbackOutboxRunner";
import { purgeLegacyFlags } from "./lib/feature-flags";
import { clearTenantScopedCaches } from "@/lib/tenant-scoped-storage";

// L.7 (2026-04-22): remove any lingering LEXIDE_EXPERIENCE /
// LEXIDE_LAYOUT / AIEDITOR_MODE localStorage keys from pre-retirement
// sessions. Fires once at module load (before any component mounts)
// so no React tree sees the stale preference.
purgeLegacyFlags();

// LexIDE is a standalone SPA hosted at its own origin, so BASE_URL is
// always "/". Keeping the strip-trailing-slash logic lets a future
// sub-path deployment slot in without code changes.
const basename = import.meta.env.BASE_URL.replace(/\/+$/, "") || "/";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/**
 * Suspense fallback for lazy routes. Matches the `OrgAdminLayout`
 * inline loader so the visual feels consistent across surfaces.
 * Mounted inside `<main>`, so the rail/sidepanel/header chrome stays
 * visible while a route chunk is fetched.
 */
/** N3 — retired detail-route shim: selects the scope the URL named, then
 *  lands on /workspace. Keeps every stale bookmark/deep-link meaningful
 *  without keeping the pages alive. */
function ScopeRedirect({ kind }: { kind: "client" | "matter" }) {
  const { clientId, matterId } = useParams<{ clientId: string; matterId?: string }>();
  useEffect(() => {
    if (!clientId) return;
    const store = useActiveContextStore.getState();
    if (kind === "matter" && matterId) store.selectMatter(clientId, matterId);
    else store.selectClient(clientId);
  }, [kind, clientId, matterId]);
  return <Navigate to="/workspace" replace />;
}

function RouteLoading() {
  return (
    <div
      data-testid="route-loading"
      className="flex flex-1 items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
    >
      <Loader2 size={16} className="animate-spin" />
      Carregando…
    </div>
  );
}

/**
 * Bridges Clerk's `useAuth().getToken()` into the module-level
 * `setClerkTokenResolver` so non-React modules (lexiun-jwt.ts,
 * lexserver-client.ts, gateway-client) can reach the Clerk JWT
 * synchronously.
 *
 * Mounted inside ClerkProvider so `useAuth()` is available; renders
 * nothing. The cleanup unsets the resolver on unmount so a stale
 * resolver doesn't hold a Clerk identity past sign-out.
 */
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setClerkTokenResolver(async () => (await getToken()) ?? null);
    return () => setClerkTokenResolver(null);
  }, [getToken]);
  return null;
}

/**
 * Route-level gate that refuses to render the LexIDE shell for
 * Clerk sessions carrying `platform.admin` scope. Such users belong
 * in LexPortal (`portal.lexiun.ai`), not in the lawyer workspace —
 * their Mongo `users.orgId` often points at a stale/terminated test
 * org (because the platform-admin identity is provisioned for portal
 * administration, not for active org work), which would otherwise
 * land them in a "Connection lost" hang while the SPA tries to talk
 * to a non-existent runtime. Mirror of `lexportal/src/App.tsx`'s
 * `<RequirePlatformAdmin>` gate, opposite direction.
 *
 * Defense-in-depth: lexserver's `/api/auth/exchange` also refuses
 * to mint for non-admins whose home org is `DELETED`/`TERMINATED`;
 * this gate covers the symmetric platform-admin case the server
 * doesn't refuse (admins still get a valid JWT — they just don't
 * belong in lexide).
 *
 * UX: when the scope check fails, the gate silently signs the user
 * out (Clerk SSO cookie is shared with portal.lexiun.ai — sign-out
 * is global by design here) and lands them on `/sign-in` so they
 * can authenticate as the correct identity. No intermediate warning
 * page: the user already typed a URL or followed a link, the right
 * action is to take them straight to where they can type the right
 * credentials.
 */
function RequireOrgUser({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<"loading" | "ok">("loading");
  const clerk = useClerk();
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Force exchange (idempotent — cached after first call) so scopes
        // are populated even when no other consumer has run yet.
        await getLexiunToken();
        const scopes = await getCurrentScopes();
        if (cancelled) return;
        if (scopes.includes("platform.admin")) {
          // Silent kick: global sign-out (clears the shared Clerk cookie)
          // + land on /sign-in. The Loader2 fallback below keeps the UI
          // from flashing the workspace shell while the sign-out resolves.
          //
          // Drop the tenant-scoped client caches on the way out. This is
          // HYGIENE, not the isolation control — it cannot repair a browser
          // that is already poisoned, and sign-out is not the only way identity
          // changes. The control is the read-side check in
          // `tenant-scoped-storage.ts`, which refuses a foreign payload however
          // it got there.
          clearTenantScopedCaches();
          await clerk.signOut({ redirectUrl: resolveAppUrl("/sign-in") });
          return;
        }
        setStatus("ok");
      } catch {
        // `getToken()` throws when no auth source is reachable. Don't
        // assume malice — let the rest of the app surface the real error.
        if (!cancelled) setStatus("ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clerk]);
  if (status === "loading") {
    return (
      <div
        data-testid="route-loading"
        className="flex h-screen items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
      >
        <Loader2 size={16} className="animate-spin" />
        Carregando…
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * `/channels` — the SETUP REGISTRY's name for the channel-connect surface,
 * which the SPA has never had a route for.
 *
 * Six of the eleven setup steps hand out `/channels?connect=…` (lexruntime
 * `extensions/lexiun-core/src/setup/step-registry.ts`: im-door,
 * im-owner-pairing, assistant-mailbox, whatsapp-door, whatsapp-owner-pairing,
 * email-rules) and EVERY one of them fell through to the catch-all — so the
 * first-run wizard's primary CTA ("Criar no Telegram") silently deposited the
 * user on the landing route instead of opening the connect dialog, dropping
 * `?onboarding=1` on the way. That is the intermittent "clicking the CTA
 * jumped to /home" report: not a race with the setup gate, just a dead route
 * plus a catch-all that used to point at `/home` and now points at `/chat`.
 *
 * `/identity` IS that surface — it reads `?connect=…&step=…` and renders
 * `ChannelConnectDialog`. `OnboardingChecklist`'s own static fallbacks already
 * pointed there, but `item.wizardRoute ?? copy.link` lets the runtime value
 * win, which is what masked the correct link.
 *
 * An SPA-side alias rather than a registry rename: the registry ships on the
 * lexruntime image axis (different lane, different deploy), so this is the
 * half that can be true immediately — and it keeps every already-deployed row
 * working if the registry is ever re-pointed later. The query string is passed
 * through verbatim so `connect`/`step`/`onboarding` all survive the hop.
 */
function ChannelsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/identity${search}`} replace />;
}

function AppShell() {
  const toggleSidePanel = useUIStore((s) => s.toggleSidePanel);
  const setSidePanelCollapsed = useUIStore((s) => s.setSidePanelCollapsed);
  const togglePalette = useCommandPaletteStore((s) => s.toggle);
  const toggleShortcutHelp = useShortcutHelpStore((s) => s.toggle);

  // L7 — auto-capture activity events (navigation/click/form/lifecycle/
  // JS-error). Mounts here so `useLocation()` resolves under <Router>.
  useActivityCapture();

  // P6 item 3 — per-case unread: routes background-session chat finals (the
  // frames `useChatSession` deliberately drops) into `useChatUnreadStore`,
  // so a ⚖ D-8 turn landing in a case nobody is watching surfaces as a dot
  // on that case's rows (OverviewNav / CommandPalette). Subscribes to the
  // module-level event bus, so provider nesting is irrelevant.
  useChatUnreadTracker();

  // Selection-as-context — mounts here so the feature is UNIVERSAL
  // across routes (case-chat, /chat global-org-scope, research,
  // anywhere a viewer renders selectable text). The hook is passive:
  // it only captures selections originating in a `[data-selectable-source]`
  // ancestor, so non-viewer text (chrome / chat history) is ignored.
  // The captured selection feeds `SelectionContextChip` in `ChatInput`
  // and (on send) the `runtime.system.enqueue_event` hidden-context
  // channel.
  useSelectionCapture();
  // Paints a persistent gold CSS Custom Highlight over the captured
  // range so the in-viewer visual anchor survives the lawyer
  // clicking into the composer textarea (which would otherwise
  // collapse the browser's native ::selection rendering). Operator
  // brief 2026-06-28 (5th refinement).
  useSelectionHighlight();

  // Phase (c) S6 — concierge → Team-of-Agents pane signalling.
  // Listens to the gateway's `session.tool` broadcast for
  // `lx_show_team_pane` + `lx_focus_mission` tool calls (both
  // shipped backend-side in Part E commit b63e6094) and reacts by
  // popping the RightSidebar + scroll-into-viewing the focused
  // mission row. Mounted at the chrome wrapper (NOT inside a route
  // layout) so it works from any mode per §B.1 goal #1 / §I.1.
  useTeamPaneCommandsFromAgent();
  // "Show, don't tell" — lets the concierge OPEN the tasklist / an agent
  // channel tab / the browser while it explains where things are.
  useGuidanceCommandsFromAgent();

  // Concierge slash-commands Part A §2.1 (NAV): subscribes to `session.tool`
  // and dispatches lx_navigate / lx_show_command_palette / lx_toggle_*_sidebar
  // / lx_show_shortcuts to the right global-chrome store. Mounted alongside
  // the team-pane hook for the same reason — works from any route.
  useNavCommandsFromAgent();

  // Concierge slash-commands Part A §2.6 (SETTINGS): dispatches
  // lx_open_settings / lx_open_org_page / lx_set_theme / lx_set_lang.
  // Theme + lang persist via their respective Zustand stores; the two
  // route verbs are plain router-navigates.
  useSettingsCommandsFromAgent();

  // Concierge slash-commands Part A §2.4 (AGENTS): dispatches
  // lx_show_mission_detail / lx_show_harness_detail to the existing
  // useUIStore detail-dialog setters. Distinct from the shipped
  // useTeamPaneCommandsFromAgent (which handles team-pane open + focus).
  useAgentDetailCommandsFromAgent();

  // Concierge slash-commands Part A §2.3 (CONTEXT): dispatches
  // lx_open_client / lx_open_matter / lx_open_case (router nav into
  // case-context) + lx_focus_context_tab (mutates the context pane's
  // active tab for the current scope). lx_run_briefing is deferred
  // to Batch 3 — see hook comment.
  useContextPaneCommandsFromAgent();

  // Concierge slash-commands Part A §2.3 (FILES, Batch 1): only
  // lx_close_file in this pass — siblings (versions/diff/info/
  // selection chip) are 🔴 store-gap decisions deferred to Batch 3.
  // Pairs with the SHIPPED useFileOpenFromAgent (lx_open_file).
  useFileViewerCommandsFromAgent();

  // Phase 7.7 — responsive shell. On mobile/tablet (<1024px), the
  // SidePanel is a fixed-position drawer (see `SidePanel.tsx`), so we
  // auto-collapse on:
  //   • viewport entering compact (resize from desktop → tablet/mobile)
  //   • every route change while compact (mobile chrome convention —
  //     navigating away from a list closes the launcher)
  //   • initial mount when compact (mount-time deps fire once)
  // We don't proactively re-open on resize-back-to-desktop; the user's
  // last manual state stands.
  const viewport = useViewportSize();
  const location = useLocation();
  const isCompact = isCompactViewport(viewport);
  useEffect(() => {
    if (isCompact) setSidePanelCollapsed(true);
  }, [isCompact, location.pathname, setSidePanelCollapsed]);

  // …and the counterpart that guarantees it BACK on a conversation surface.
  // The comment above ("the user's last manual state stands") was written for
  // MANUAL collapses; without this, the three AUTOMATIC collapse paths left the
  // navigator column gone for the rest of the session with no visible way back.
  // The hook itself honours an explicit user collapse — see its docblock.
  useSidePanelEntryGuarantee();

  // Operator directive 2026-06-24 — auto-collapse the left main
  // Sidebar (Lexiun nav) when the user FIRST enters a case workspace,
  // so the case-file tree gets room to breathe. Fire only on the
  // transition (null caseId → set caseId) so we don't fight the user
  // if they re-expand the sidebar mid-session. Tracking the previous
  // value in a ref keeps the dependency array small + the effect from
  // running on every unrelated selection mutation.
  const activeCaseId = useActiveContextStore((s) => s.selection?.caseId ?? null);
  const prevActiveCaseIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeCaseId && !prevActiveCaseIdRef.current) {
      useUIStore.getState().setSidebarExpanded(false);
    }
    prevActiveCaseIdRef.current = activeCaseId;
  }, [activeCaseId]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        toggleSidePanel();
      }
      // Cmd+K (Mac) / Ctrl+K (Windows/Linux) toggles the universal command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        togglePalette();
      }
      // Phase 7.4: `?` toggles the shortcut help overlay. Skip when
      // typing in an input — `?` is a printable character so we must
      // not steal keystrokes from form fields. Also require no
      // modifiers: on some keyboard layouts `Ctrl+Shift+/` produces
      // `key === "?"` and we don't want to shadow real browser
      // shortcuts.
      if (
        e.key === "?" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTypingTarget(e)
      ) {
        e.preventDefault();
        toggleShortcutHelp();
      }
      // Phase 7.4 (Q10): Cmd+/ (Mac) / Ctrl+/ (Windows/Linux) is an
      // alias for the help overlay. The `?` shortcut requires Shift
      // on US layouts and is awkward to reach on AZERTY / many other
      // layouts where `/` lives in a different position; the modifier
      // form is universal across layouts. Works inside form fields
      // too (modifier shortcut, doesn't shadow plain text input).
      if ((e.metaKey || e.ctrlKey) && e.key === "/" && !e.altKey) {
        e.preventDefault();
        toggleShortcutHelp();
      }
      // Phase 7.4: single registration point for Esc-close on
      // global overlays. Today this is largely redundant — cmdk's
      // command palette and Radix's shortcut-help dialog both
      // self-close on Esc through their own keydown listeners — so
      // calling `setOpen(false)` here is idempotent. The value is
      // forward-looking: a future overlay that doesn't trap focus
      // inside an input (e.g. a non-modal drawer) would otherwise
      // ignore Esc, and registering it here makes it close
      // alongside everything else. We don't `preventDefault()` so
      // cmdk/Radix teardown still runs.
      if (e.key === "Escape" && !isTypingTarget(e)) {
        useCommandPaletteStore.getState().setOpen(false);
        useShortcutHelpStore.getState().setOpen(false);
        // Phase 7.7 — Esc also dismisses the mobile/tablet side-panel
        // drawer (no-op on desktop because the panel is in-flow there
        // and Esc would close it unexpectedly). Reads viewport from
        // window directly because the effect's closure pins the
        // hook-derived `isCompact` value at mount time.
        if (
          typeof window !== "undefined" &&
          isCompactViewport(classifyViewport(window.innerWidth)) &&
          !useUIStore.getState().sidePanelCollapsed
        ) {
          // Esc is the lawyer dismissing the drawer — user intent, so it
          // stamps and the entry guarantee will not re-open behind them.
          useUIStore.getState().setSidePanelCollapsedByUser(true);
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidePanel, togglePalette, toggleShortcutHelp]);

  return (
    <QueryClientProvider client={queryClient}>
    {/* SelectionUrlSync only depends on React Router, not on the
        gateway — mounted outside GatewayProvider so its dependency
        graph is explicit. Same for WorkspaceFileUrlSync (L.3). */}
    <SelectionUrlSync />
    {/* Persists the active scope to the server (/api/lx/context) + passively
        resumes the last-active case on a fresh-tab landing (CCM Handoff #1,
        Surface A). Router-only deps, like SelectionUrlSync. */}
    <LastActiveContextSync />
    {/* Always-on: feeds the chat infobar's mission ticker from the runtime
        push. Renders nothing; suppresses itself while the Agentes pane is
        on screen (I-4). MUST stay a sibling here, inside the Router, like
        the gates around it. */}
    <MissionTickerBridge />
    <WorkspaceFileUrlSync />
    <GatewayProvider>
    <PlatformOpsProvider>
    <CommandPalette />
    {/* CCM Handoff #2 §2.3 — the deterministic case-name switch dialog,
        mounted at shell level so it's reachable from any route (incl.
        /home + /chat) via the always-on ContextIndicator. */}
    <ShortcutHelpDialog />
    <RetryPromptHost />
    <FeedbackOutboxRunner />
    <ActivityFlusher />
    {/* Phase 7.7 — mobile shell: `flex-col` stacks children vertically.
        ActivityBar carries `order-last` so it sits at the BOTTOM of the
        column on mobile (instead of the top, which would be a bad UX
        — users expect the launcher rail at the bottom on mobile). On
        ≥768px (`md:`), revert to `flex-row` for the historic three-
        column shell. SidePanel is fixed-position on mobile/tablet, so
        it doesn't take any flex space below 1024px and the column
        contains just the main column + the bottom bar. */}
    <div className="flex h-screen flex-row bg-cream-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <NavigationBridgeInstaller />
      {/* The nav rail yields whenever panes compete for width (focus pane,
          file viewer, Agents sidebar) and comes back when they clear. */}
      <RailSpaceGovernor />
      <SetupGate />
      <SetupReminderGate />
      <ActivityBar />
      <SidePanel />
      {/* Phase (c) right sidebar — Team of Agents host. Sibling of
          ActivityBar + SidePanel (NOT inside any route layout) so it
          survives route transitions. §B.1 goal #1 of
          ai-docs/lexide-right-sidebar-implementation-plan.md. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <GatewayDisconnectedBanner />
        {/* CCM Handoff #2 §2.1 — the always-on "you are here" scope bar.
            Hoisted here (from inside CaseWorkspaceLayout) so it renders on
            EVERY route — /home, /chat, and every case page — always showing
            the active scope (a clear "Global / General" state when nothing
            is selected) + the stale-selection cue + Resume + Switch. Slim,
            unobtrusive, top-of-chrome. */}
        <div className="shrink-0 border-b border-slate-200/70 bg-slate-50/60 dark:border-slate-800/70 dark:bg-slate-900/30">
          <ContextIndicator />
        </div>
        <main className="flex flex-1 overflow-hidden">
          <RouteErrorBoundary>
          <Suspense fallback={<RouteLoading />}>
          <Routes>
            {/* The welcome screen is RETIRED (operator 2026-08-04). `/home` is kept as a
                REDIRECT rather than deleted: the rail entry, post-signup redirect and
                old links all still point here, and a 404 for them would be a worse
                regression than a hop. HomeLayout must not RENDER — it collapses the
                workspace navigator on every render (its "welcome focus mode"), which
                is half of the defect pair this retires. */}
            <Route path="/home" element={<Navigate to="/chat" replace />} />
            <Route path="/home/:conversationId" element={<Navigate to="/chat" replace />} />
            {/* Lexiun's single, persistent global chat thread — and, since the
             *  welcome retirement, the destination of every neutral landing.
             *  A boot resume into the last-active CASE still overrides it
             *  (`LastActiveContextSync`, D-2). */}
            <Route path="/chat" element={<GlobalConversationLayout />} />
            {/* Setup-registry alias — see `ChannelsRedirect`. */}
            <Route path="/channels" element={<ChannelsRedirect />} />
            {/* D6: Workspace mode landing — Client → Matter → Case tree */}
            <Route path="/workspace" element={<WorkspaceLayout />} />
            {/* N3 (plan 1182f2034 §3): the D7/D8a detail PAGES are RETIRED —
                everything they carried lives in the ClientAttributesDialog /
                MatterAttributesDialog now. Old links land on /workspace with
                the scope SELECTED (ScopeRedirect), so a stale bookmark still
                puts the lawyer in the right place. The long CASE route below
                stays — it is an alias, not a detail page. */}
            <Route path="/clients/:clientId" element={<ScopeRedirect kind="client" />} />
            <Route
              path="/clients/:clientId/matters/:matterId"
              element={<ScopeRedirect kind="matter" />}
            />
            {/* N1 (plan 1182f2034 §3): the D6-era flat list is RETIRED — its own
                route comment said "remove after D7" since April. /workspace is
                the all-cases surface; old bookmarks land there. The exact-match
                redirect leaves /cases/recycle-bin + /cases/:caseId untouched. */}
            <Route path="/cases" element={<Navigate to="/workspace" replace />} />
            {/* The Cases Recycle Bin lives in the CASES area, not settings:
                restore is available to anyone who can delete, so an
                admin-shaped location would hide recovery from the users
                most likely to need it (handoff:cases-recycle-bin). NOTE it
                must precede the /cases/:caseId route or the resolver would
                treat "recycle-bin" as a caseId. */}
            <Route path="/cases/recycle-bin" element={<RecycleBinLayout />} />
            {/* D5a: short-URL canonical — resolver bootstraps parents via case.locate */}
            <Route path="/cases/:caseId/*" element={<CaseRouteResolver />} />
            {/* D6: long-URL canonical — parents come straight from the URL (no locate hop) */}
            <Route
              path="/clients/:clientId/matters/:matterId/cases/:caseId/*"
              element={<LongUrlCaseWorkspace />}
            />
            {/* D8b: dev-only test probe for matter-tab-registry + caseLabelFor. */}
            {import.meta.env.DEV && (
              <Route path="/__probe/matter-tabs" element={<TabRegistryProbe />} />
            )}
            {/* D9a: dev-only test probe for AssignmentList rendering. */}
            {import.meta.env.DEV && (
              <Route path="/__probe/assignments" element={<AssignmentsProbe />} />
            )}
            {/* L.2: dev-only probe for the ActiveContextPane shell. */}
            {import.meta.env.DEV && (
              <Route
                path="/__probe/active-context-pane"
                element={<ActiveContextPaneProbe />}
              />
            )}
            {/* L.3: dev-only probe for the WorkspaceFilePane shell. */}
            {import.meta.env.DEV && (
              <Route
                path="/__probe/workspace-file-pane"
                element={<WorkspaceFilePaneProbe />}
              />
            )}
            {/* L.6: dev-only probe for useDerivedChatSessionKey. */}
            {import.meta.env.DEV && (
              <Route
                path="/__probe/derived-session-key"
                element={<DerivedSessionKeyProbe />}
              />
            )}
            {/* C.2: dev-only probe for the matter-type widget registry. */}
            {import.meta.env.DEV && (
              <Route
                path="/__probe/case-widgets"
                element={<CaseWidgetsProbe />}
              />
            )}
            <Route path="/contacts" element={<ContactsLayout />} />
            <Route path="/contacts/:contactId" element={<ContactsLayout />} />
            {/* Pendências — the user tasklist (gate-off hides it; the layout
                bounces a hand-typed URL to /home when disabled). */}
            <Route path="/tasks" element={<TasklistLayout />} />
            <Route path="/schedule" element={<ScheduleLayout />} />
            <Route path="/identity" element={<IdentityLayout />} />
            <Route path="/activity" element={<ActivityLayout />} />
            <Route path="/activity/calendar" element={<ActivityLayout />} />
            <Route path="/org/*" element={<OrgAdminLayout />} />
            <Route path="/agent-browser" element={<AgentBrowserLayout />} />
            <Route path="/agent" element={<AgentLayout />} />
            <Route path="/settings" element={<SettingsLayout />} />
            <Route path="/" element={<Navigate to="/chat" replace />} />
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
      {/* Claude-web-style file viewer — sits as a sibling between the
          main column and the right Lexiun Agents pane so it gets the
          FULL viewport height (not nested under case-header / breadcrumbs)
          and animates 0 → ~50vw width on open. Returns null when no
          file is active; on compact viewports it switches to a full-
          bleed fixed overlay. See WorkspaceFilePaneShell for details. */}
      <WorkspaceFilePaneShell />
      {/* The FOCUS PANE — a working surface (tasklist / browser / contacts)
          beside the conversation, so the lawyer can drive it by prompt and
          watch it change. Opening collapses the other columns; closing
          restores them. Surfaces come from focus-pane-registry.tsx. */}
      <FocusPaneHost />
      <RightSidebar />
      {/* §K-bis detail dialogs — mounted at shell level so any
          row click in the Lexiun Agents pane (or anywhere else that
          calls `useUIStore.openMissionDetail`/`openHarnessDetail`)
          opens the magnifier-glass view. One of each at a time
          (the store enforces mutual exclusion). */}
      <MissionDetailDialog />
      <HarnessDetailDialog />
    </div>
    </PlatformOpsProvider>
    </GatewayProvider>
    </QueryClientProvider>
  );
}

/**
 * D6: long-URL canonical route wrapper. Pulls parent ids straight
 * from the URL and passes them into CaseWorkspaceLayout as props,
 * bypassing the case.locate hop that the short-URL path needs.
 */
function LongUrlCaseWorkspace() {
  const { clientId, matterId, caseId } = useParams<{
    clientId: string;
    matterId: string;
    caseId: string;
  }>();
  return <CaseWorkspaceLayout clientId={clientId} matterId={matterId} caseId={caseId} />;
}

function AppInner() {
  // Wrap with ClerkProvider + route protection only if a publishable key
  // is configured. This keeps local dev working without Clerk.
  if (clerkPk) {
    return (
      <ClerkProvider
        publishableKey={clerkPk}
        signInUrl={resolveAppUrl("/sign-in")}
        signUpUrl={resolveAppUrl("/sign-up")}
        afterSignOutUrl={resolveAppUrl("/sign-in")}
      >
        <BrowserRouter basename={basename}>
          <Routes>
            {/* Public auth routes — no shell */}
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />

            {/* Protected routes — requires Clerk sign-in */}
            <Route
              path="/*"
              element={
                <>
                  <SignedIn>
                    <ClerkTokenBridge />
                    <UserActivityProvider>
                      <RequireOrgUser>
                        <AppShell />
                      </RequireOrgUser>
                    </UserActivityProvider>
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
          </Routes>
        </BrowserRouter>
      </ClerkProvider>
    );
  }

  // Local dev mode — no login wall, direct access. We still serve the
  // /sign-in and /sign-up routes so the AuthLayout chrome (lexsite atmosphere
  // + EAP backlinks) is reviewable end-to-end without a Clerk publishable
  // key; the pages internally fall back to a placeholder when clerkPk is
  // absent so the Clerk widget never instantiates without a provider.
  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        <Route
          path="/*"
          element={
            <UserActivityProvider>
              <AppShell />
            </UserActivityProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <AppInner />
    </Sentry.ErrorBoundary>
  );
}
